import { Component } from '@angular/core';
import { App, RestoredListenerEvent } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { NavController, Platform, AlertController } from '@ionic/angular';
import OneSignal from 'onesignal-cordova-plugin';
import { environment } from 'src/environments/environment.prod';
import { register } from 'swiper/element/bundle';
import { AUTH, REGISTER } from './constants/routes.const';
import { AppResumeService } from './services/app-resume.service';
import { AuthService } from './services/firebase/auth.service';
import { UserService } from './services/user/user.service';
import { FirebaseRemoteConfig } from '@capacitor-firebase/remote-config';

register();

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent {

  public production = environment.production;
  public appVersion: string

  constructor(
    private authService: AuthService,
    private userService: UserService,
    private navCtrl: NavController,
    private platform: Platform,
    private appResumeService: AppResumeService,
    private alertController: AlertController
  ) {
    this.setupUser();
    if(Capacitor.isNativePlatform()) {
      this.setupOneSignal();
      this.setupBackButton();

      if(this.platform.is('android')) {
        App.addListener('appRestoredResult', (event: RestoredListenerEvent) => {
          console.log('appRestoredResult->event', event)
          this.appResumeService.restoreAppState(event);
        });
      }

      this.checkAppVersion();

      if(!this.production) {
        // this.getAppVersion();
      }
    }
  }

  setupUser() {
    this.authService.authStateChange();
    this.userService.setupUser();

    this.userService.currentUser.subscribe( currentUser => {
      // Visitantes (anônimos) não precisam ser redirecionados para completar cadastro
      if (this.authService.currentUser?.isAnonymous) {
        return;
      }

      if(currentUser?.isCompletedRegister === false) {
        this.navCtrl.navigateRoot([AUTH, REGISTER], {queryParams: {completeRegister: true}});
      } else if(currentUser?.isCompletedRegister === true) {
        if(Capacitor.isNativePlatform()) {
          OneSignal.setExternalUserId(currentUser.uid);
          setTimeout(() => {
            OneSignal.promptForPushNotificationsWithUserResponse((accepted) => {
              // console.log("User accepted notifications: " + accepted);
            });
          }, 5000);
        }
      }
    })
  }

  setupOneSignal() {
    OneSignal.setAppId(environment.onesignal_app_id);

    OneSignal.setNotificationOpenedHandler((jsonData) => {
      const { additionalData }: any = jsonData?.notification;
      if(additionalData?.type == 'route') {
        setTimeout(() => this.navCtrl.navigateRoot([additionalData.data]), 220);
      }
    });
  }

  setupBackButton() {
    App.addListener('backButton', ({canGoBack}) => {
      if(canGoBack) {
        this.navCtrl.back();
      } else {
        App.exitApp();
      }
    });
  }

  async getAppVersion() {
    if(Capacitor.isNativePlatform()) {
      const { version } = await App.getInfo();
      this.appVersion = version ?? 'WEB';
    }

    const alert = await this.alertController.create({
      header: 'Atualização Necessária',
      message: 'Para continuar usando o app, você precisa instalar a versão mais recente.',
      backdropDismiss: false, // Impede o usuário de fechar o alerta
      buttons: [
        {
          text: 'Atualizar Agora',
          handler: () => {
            // Redireciona para a Play Store
            window.open('market://details?id=br.com.estudeivendi.loja', '_system');
          }
        }
      ]
    });

    await alert.present();
  }

  async checkAppVersion() {
    try {
      // 1. Ativa e busca as configurações mais recentes do Remote Config
      await FirebaseRemoteConfig.fetchAndActivate();

      // 2. Define qual chave buscar dependendo da plataforma
      const isAndroid = this.platform.is('android');
      const remoteKey = isAndroid ? 'min_required_version_code' : 'min_required_build_number_ios';

      // 3. Obtém o número mínimo da build requerido que configuramos no Firebase
      const result = await FirebaseRemoteConfig.getNumber({ key: remoteKey });
      const minRequiredBuild = result.value;

      // Se não houver valor configurado, não faz nada
      if (!minRequiredBuild) return;

      // 4. Obtém as informações do app instalado no dispositivo
      const appInfo = await App.getInfo();
      const currentBuild = parseInt(appInfo.build, 10);

      // 5. Compara a build atual com a mínima requerida
      if (!isNaN(currentBuild) && !isNaN(minRequiredBuild)) {
        if (currentBuild < minRequiredBuild) {
          this.showUpdateDialog();
        }
      } else {
        console.warn('Build inválida detectada:', currentBuild, minRequiredBuild);
      }
    } catch (error) {
      console.error('Erro ao verificar a versão do app:', error);
      // Em caso de erro (ex: sem internet), decida se bloqueia o app ou não.
      // Para uma mudança crítica, é mais seguro bloquear.
    }
  }

  async showUpdateDialog() {
    const isAndroid = this.platform.is('android');
    // Para iOS, usa a App Store. Para Android, usa a Play Store.
    const storeUrl = isAndroid 
      ? 'market://details?id=br.com.estudeivendi.loja'
      : 'itms-apps://itunes.apple.com/app/id6460590379';

    const storeName = isAndroid ? 'Play Store' : 'App Store';

    const alert = await this.alertController.create({
      header: 'Atualização Necessária',
      message: `Para continuar usando o app, você precisa instalar a versão mais recente disponível na ${storeName}.`,
      backdropDismiss: false, // Impede o usuário de fechar o alerta
      buttons: [
        {
          text: 'Atualizar Agora',
          handler: () => {
            // Redireciona o usuário para a página do app na loja
            window.open(storeUrl, '_system');
          },
        },
      ],
    });

    await alert.present();
  }
}
